"use client";

import { type ReactNode, useMemo, useState } from "react";
import { PillGroup, TextField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  NOTICE,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";
import {
  F_ALPHAS,
  F_DF1,
  F_DF2,
  fCritical,
  fLowerCritical,
  isValidAlpha,
  isValidDf,
} from "@/lib/tool-kit/stats-distributions/tables";

/** English strings. A translated content file overrides these through `ui`. */
export const F_TABLE_STRINGS = {
  tableAlphaLabel: "Table for α =",
  df1Label: "Numerator df (df₁)",
  df2Label: "Denominator df (df₂)",
  alphaLabel: "Significance level (α)",
  lookupHeading: "Critical value",
  result: "F = {value}",
  explain:
    "With {df1} and {df2} df, an area of {alpha} lies above {value}: reject H₀ when F ≥ {value}.",
  lowerLine:
    "Lower critical value, with an area of {alpha} below it: {value} — that is, 1 / F({alpha}; {df2}, {df1}), for two-sided tests of a variance ratio.",
  enterDf:
    "Enter both degrees of freedom as numbers greater than 0 — decimals are fine.",
  enterAlpha: "Enter α between 0 and 1, such as 0.05.",
  caption: "Critical values of F for α = {alpha} (area to the right)",
  df1Header: "Numerator degrees of freedom (df₁)",
  df2Header: "df₂",
  print: "Print table",
  printNote:
    "Columns are the numerator degrees of freedom (df₁, between groups in ANOVA) and rows the denominator df (df₂, within groups). ∞ is the limit as the degrees of freedom grow without bound.",
} as const;

type Strings = typeof F_TABLE_STRINGS;

const HIGHLIGHT = "bg-primary/15 font-semibold text-foreground";
const HEADER_HIGHLIGHT = "bg-primary/10";

function dfText(df: number, fmt: NumberFormatter): string {
  return df === Number.POSITIVE_INFINITY ? "∞" : fmt(df);
}

/** Three decimals, two for the four-digit values in the df₂ = 1 row. */
function tableValue(value: number, fmt: NumberFormatter): string {
  const decimals = value >= 1000 ? 2 : 3;
  return fmt(value, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function FTableBlock({
  alpha,
  highlightRow,
  highlightColumn,
  strings,
  fmt,
}: {
  alpha: number;
  highlightRow: number;
  highlightColumn: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const values = useMemo(
    () => F_DF2.map((df2) => F_DF1.map((df1) => fCritical(df1, df2, alpha))),
    [alpha]
  );
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card print:overflow-visible">
      <table className="w-full border-collapse font-mono text-xs tabular-nums print:text-[7pt]">
        <caption className="px-3 pt-3 pb-1 text-left font-medium font-sans text-foreground text-sm">
          {t(strings.caption, {
            alpha: fmt(alpha, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 3,
            }),
          })}
        </caption>
        <thead>
          <tr>
            <td className="sticky left-0 bg-card" />
            <th
              className="px-2 pt-1.5 text-center font-medium font-sans text-muted-foreground text-xs"
              colSpan={F_DF1.length}
              scope="colgroup"
            >
              {strings.df1Header}
            </th>
          </tr>
          <tr className="border-border border-b">
            <th
              className="sticky left-0 bg-card px-2 py-1.5 text-left font-sans font-semibold"
              scope="col"
            >
              {strings.df2Header}
            </th>
            {F_DF1.map((df1, column) => (
              <th
                className={`whitespace-nowrap px-1.5 py-1.5 text-right font-semibold ${column === highlightColumn ? HEADER_HIGHLIGHT : ""}`}
                key={df1}
                scope="col"
              >
                {dfText(df1, fmt)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {F_DF2.map((df2, row) => (
            <tr className="border-border/60 border-b last:border-0" key={df2}>
              <th
                className={`sticky left-0 whitespace-nowrap bg-card px-2 py-1 text-left font-semibold ${row === highlightRow ? HEADER_HIGHLIGHT : ""}`}
                scope="row"
              >
                {dfText(df2, fmt)}
              </th>
              {F_DF1.map((df1, column) => (
                <td
                  className={`whitespace-nowrap px-1.5 py-1 text-right text-muted-foreground ${row === highlightRow && column === highlightColumn ? HIGHLIGHT : ""}`}
                  key={df1}
                >
                  {tableValue(values[row][column], fmt)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function lookupBody(
  df1: number | null,
  df2: number | null,
  alpha: number | null,
  strings: Strings,
  fmt: NumberFormatter
): ReactNode {
  if (df1 === null || df2 === null) {
    return <p className="text-muted-foreground">{strings.enterDf}</p>;
  }
  if (alpha === null) {
    return <p className="text-muted-foreground">{strings.enterAlpha}</p>;
  }
  const four = (value: number) =>
    fmt(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  const values = {
    df1: fmt(df1, { maximumFractionDigits: 4 }),
    df2: fmt(df2, { maximumFractionDigits: 4 }),
    alpha: fmt(alpha, { maximumFractionDigits: 6 }),
    value: four(fCritical(df1, df2, alpha)),
  };
  return (
    <>
      <p className="font-mono font-semibold text-2xl text-foreground">
        {t(strings.result, values)}
      </p>
      <p className="text-muted-foreground">{t(strings.explain, values)}</p>
      <p className="text-muted-foreground">
        {t(strings.lowerLine, {
          ...values,
          value: four(fLowerCritical(df1, df2, alpha)),
        })}
      </p>
    </>
  );
}

function readDf(text: string): number | null {
  const value = parseNumberInput(text);
  return value !== null && isValidDf(value) ? value : null;
}

export default function FTableWidget() {
  const strings = useToolUi(F_TABLE_STRINGS);
  const fmt = useToolNumber();
  const [tableAlpha, setTableAlpha] = useState("0.05");
  const [df1Text, setDf1Text] = useState("5");
  const [df2Text, setDf2Text] = useState("10");
  const [alphaInput, setAlphaInput] = useState("0.05");

  const df1 = readDf(df1Text);
  const df2 = readDf(df2Text);
  const parsedAlpha = parseNumberInput(alphaInput);
  const alpha =
    parsedAlpha !== null && isValidAlpha(parsedAlpha) ? parsedAlpha : null;
  const shownAlpha = Number(tableAlpha);
  const sameAlpha = alpha !== null && Math.abs(alpha - shownAlpha) < 1e-12;
  const highlightRow = sameAlpha && df2 !== null ? F_DF2.indexOf(df2) : -1;
  const highlightColumn = sameAlpha && df1 !== null ? F_DF1.indexOf(df1) : -1;

  function chooseTable(value: string) {
    setTableAlpha(value);
    setAlphaInput(value);
  }

  return (
    <>
      <section className="space-y-5 print:hidden">
        <PillGroup
          label={strings.tableAlphaLabel}
          name="ft-alpha"
          onChange={chooseTable}
          options={F_ALPHAS.map((value) => ({
            value: String(value),
            label: fmt(value, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 3,
            }),
          }))}
          value={tableAlpha}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id="ft-df1"
            label={strings.df1Label}
            numeric
            onChange={setDf1Text}
            value={df1Text}
          />
          <TextField
            id="ft-df2"
            label={strings.df2Label}
            numeric
            onChange={setDf2Text}
            value={df2Text}
          />
          <TextField
            id="ft-alpha-lookup"
            label={strings.alphaLabel}
            numeric
            onChange={setAlphaInput}
            value={alphaInput}
          />
        </div>
      </section>
      <div
        aria-live="polite"
        className={`${CARD} space-y-2 text-sm print:hidden`}
      >
        <h2 className={WIDGET_H2}>{strings.lookupHeading}</h2>
        {lookupBody(df1, df2, alpha, strings, fmt)}
      </div>
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <p className={`${NOTICE} flex-1`}>{strings.printNote}</p>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
        </div>
        <FTableBlock
          alpha={shownAlpha}
          fmt={fmt}
          highlightColumn={highlightColumn}
          highlightRow={highlightRow}
          strings={strings}
        />
      </section>
    </>
  );
}
